import { Box, ButtonBase, Grid, IconButton, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import {
  HiOutlineCheckCircle,
  HiOutlineDocumentDuplicate,
  HiOutlineEye,
  HiOutlinePencilAlt,
  HiOutlineTrash,
} from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import {
  cloneInvoiceTemplate,
  deleteInvoiceTemplate,
  getInvoiceTemplate,
  getWorkspaceSettings,
  InvoiceTemplateDTO,
  updateDefaultTemplate,
  WorkspaceSettingsDTO,
} from 'wisestep-api';
import { WsColors, WsTitleBanner, WsTypoColors, WsTypography, WsButton } from 'wisestep-base';

import { useDialog } from '../../../hooks/useDialog';
import { useFetch } from '../../../hooks/useFetch';
import { InvoicingAction, useInvoicingAuthz } from '../../../hooks/useInvoicingAuthz';
import { failed, success } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import ConfirmationDialog from '../../clientDetails/components/ConfirmationDialog';

import FormstackIntegration from './FormstackIntegration';
import { InvoiceTemplate } from './InvoiceTemplateForm';

const TemplateBox = styled(Box)(() => ({
  border: '0.125rem solid lightgray',
  backgroundColor: 'white',
  aspectRatio: '0.707',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '4px',
}));

const templateNameStyle = {
  flex: '1',
  textAlign: 'center',
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
};

export const ManageInvoiceTemplates = ({
  selectable = false,
  onSelect,
}: {
  selectable?: boolean;
  onSelect?(template: InvoiceTemplate): void;
}) => {
  const navigate = useNavigate();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [templates, setTemplates] = useFetch<InvoiceTemplateDTO[]>(getInvoiceTemplate);
  const [workspaceSettings, setWorkspaceSettings] =
    useFetch<WorkspaceSettingsDTO>(getWorkspaceSettings);
  const [openDialog, closeDialog] = useDialog();
  const canAddTemplate = useInvoicingAuthz(InvoicingAction.ADD_INVOICE_TEMPLATE);
  const canEditTemplate = useInvoicingAuthz(InvoicingAction.EDIT_INVOICE_TEMPLATE);
  const [onEdit, setOnEdit] = useState<boolean>(false);
  const [formstackKeys, setFormstackKeys] = useState<{
    id: string | null;
    name: string;
    formstackUrl: string;
    placementType: string;
    ourEntityIds: string;
    invoiceTypeIds: string;
  }>({
    id: null,
    name: '',
    formstackUrl: '',
    placementType: '',
    ourEntityIds: '',
    invoiceTypeIds: '',
  });

  const handleEditClick = (template) => {
    setFormstackKeys(template);
    setOnEdit(true);
  };

  const deleteInvoice = async (template) => {
    try {
      const { data } = await deleteInvoiceTemplate(template.id ?? '');
      setTemplates(data);
      setNotification(success('Template deleted successfully!'));
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
    closeDialog();
  };

  const cloneInvoiceTemplates = async (template) => {
    try {
      const { data } = await cloneInvoiceTemplate(template.id ?? '');
      setTemplates(data);
      setNotification(success('Template cloned successfully!'));
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
  };

  const defaultInvoiceTemplates = async (template) => {
    try {
      const { data } = await updateDefaultTemplate(template.id ?? '');
      setWorkspaceSettings(data);
      setNotification(success('Default Template updated!'));
    } catch (e) {
      setNotification(failed('Some error occurred. Please try later'));
    }
  };

  return (
    <>
      {onEdit ? (
        <FormstackIntegration
          id={formstackKeys.id ? formstackKeys?.id : null}
          name={formstackKeys?.name ? formstackKeys?.name : ''}
          formstackUrl={formstackKeys?.formstackUrl ? formstackKeys?.formstackUrl : ''}
          placementType={formstackKeys?.placementType ? formstackKeys?.placementType : ''}
          ourEntityIds={formstackKeys?.ourEntityIds ? formstackKeys?.ourEntityIds : ''}
          invoiceTypeIds={formstackKeys?.invoiceTypeIds ? formstackKeys?.invoiceTypeIds : ''}
        />
      ) : (
        <Box sx={{ backgroundColor: WsColors.TheBlues25 }}>
          {!selectable && <WsTitleBanner title="Select Template" />}
          <Grid container spacing={6} p={6}>
            {canAddTemplate && !selectable && (
              <Grid
                item
                xs={2}
                style={{ display: 'flex', justifyContent: 'flex-end', marginLeft: '120vh' }}
              >
                <WsButton variant="contained" size="medium" onClick={() => navigate('select')}>
                  Create Template
                </WsButton>
              </Grid>
            )}
            {templates?.map((template, index) => (
              <Grid item xs={4} xl={3} key={index}>
                <TemplateBox>
                  {selectable ? (
                    <ButtonBase sx={templateNameStyle} onClick={() => onSelect?.(template)}>
                      <WsTypography bold cursor="pointer" color={WsTypoColors.PRIMARY600}>
                        Choose template
                      </WsTypography>
                    </ButtonBase>
                  ) : (
                    <Box sx={templateNameStyle}></Box>
                  )}{' '}
                  <Grid item style={{ display: 'flex', justifyContent: 'center' }}>
                    <WsTypography bold cursor="pointer" color={WsTypoColors.PRIMARY600}>
                      Template Name : {template.name}
                    </WsTypography>
                  </Grid>
                  {!selectable && (
                    <Stack direction="row" justifyContent="center" spacing={1} p={1}>
                      <Tooltip title="View">
                        <IconButton
                          onClick={() => navigate(template.id ?? '', { state: { readonly: true } })}
                        >
                          <HiOutlineEye />
                        </IconButton>
                      </Tooltip>

                      {canEditTemplate && (
                        <Tooltip title="Edit">
                          <IconButton onClick={() => handleEditClick(template)}>
                            <HiOutlinePencilAlt />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          onClick={() => {
                            openDialog({
                              children: (
                                <ConfirmationDialog
                                  prompt="Are you sure you want to delete the invoice template?"
                                  confirmText="Delete"
                                  skipText="Cancel"
                                  onConfirm={() => deleteInvoice(template)}
                                  onSkip={closeDialog}
                                />
                              ),
                            });
                          }}
                        >
                          <HiOutlineTrash />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Duplicate">
                        <IconButton onClick={() => cloneInvoiceTemplates(template)}>
                          <HiOutlineDocumentDuplicate />
                        </IconButton>
                      </Tooltip>
                      {workspaceSettings?.defaultTemplate?.id !== template.id && canAddTemplate && (
                        <Tooltip title="Mark default">
                          <IconButton onClick={() => defaultInvoiceTemplates(template)}>
                            <HiOutlineCheckCircle />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </TemplateBox>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </>
  );
};
